import React from 'react';
import { useNavigate } from 'react-router-dom';
import useSetupChecks from 'components/CompanySetupChecklist';

/**
 * Soft-guide first-run page after add-company / from dashboard checklist.
 * Ordered steps; does not block payroll create.
 */
function GetReadyForPayroll() {
    const navigate = useNavigate();
    const { checks, requiredDone, requiredTotal, allRequiredDone, missingRequired } = useSetupChecks();
    const ordered = [...checks].sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
    const required = ordered.filter(c => c.required);
    const optional = ordered.filter(c => !c.required);
    const pct = Math.round((requiredDone / Math.max(requiredTotal, 1)) * 100);

    const go = (item) => {
        if (item.link) navigate(item.link);
        else navigate(`/dashboard/manage-company${item.hash ? '#' + item.hash : ''}`);
    };

    return (
        <div className="content" style={{ padding: '24px 28px', maxWidth: 820 }}>
            <div style={{ marginBottom: 20 }}>
                <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard')}
                        style={{ background: 'none', border: 'none', color: '#1B75F0', cursor: 'pointer', padding: 0, fontSize: 13 }}
                    >
                        Dashboard
                    </button>
                    {' / Get ready for payroll'}
                </p>
                <h2 style={{ margin: '8px 0 4px', fontWeight: 800, color: '#1a3a5c', fontSize: 22 }}>
                    Get ready for payroll
                </h2>
                <p style={{ margin: 0, fontSize: 14, color: '#6b7280', maxWidth: 560 }}>
                    Complete the required steps below. You can create draft payroll anytime;
                    approving with direct deposit requires company funding and these required items.
                </p>
            </div>

            <div style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                padding: '16px 20px', marginBottom: 20,
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                        {allRequiredDone
                            ? 'Required setup complete'
                            : `${requiredDone} of ${requiredTotal} required · ${missingRequired.length} remaining`}
                    </span>
                    {allRequiredDone && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => navigate('/dashboard/start-new-payroll')}
                        >
                            Start payroll
                        </button>
                    )}
                </div>
                <div style={{ background: '#f3f4f6', borderRadius: 99, height: 8, overflow: 'hidden' }}>
                    <div style={{
                        height: '100%', width: `${pct}%`, borderRadius: 99,
                        background: allRequiredDone ? '#16a34a' : '#1B75F0',
                        transition: 'width 0.3s ease',
                    }} />
                </div>
            </div>

            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1a3a5c', marginBottom: 10 }}>Required</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {required.map((item, idx) => (
                    <StepRow key={item.id} item={item} index={idx + 1} onClick={() => go(item)} />
                ))}
            </div>

            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#6b7280', marginBottom: 10 }}>Optional</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {optional.map(item => (
                    <StepRow key={item.id} item={item} onClick={() => go(item)} optional />
                ))}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/dashboard')}>
                    Back to dashboard
                </button>
                <button type="button" className="btn btn-primary" onClick={() => navigate('/dashboard/start-new-payroll')}>
                    {allRequiredDone ? 'Start payroll' : 'Go to payroll anyway'}
                </button>
            </div>
        </div>
    );
}

function StepRow({ item, index, onClick, optional }) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
                width: '100%', padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                background: item.done ? '#f0fdf4' : '#fff',
                border: `1px solid ${item.done ? '#bbf7d0' : '#e5e7eb'}`,
            }}
        >
            <span style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700,
                background: item.done ? '#16a34a' : (optional ? '#f3f4f6' : '#EFF6FF'),
                color: item.done ? '#fff' : (optional ? '#9ca3af' : '#1B75F0'),
            }}>
                {item.done ? '✓' : (index ?? '○')}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: item.done ? '#15803d' : '#1f2937' }}>
                    {item.label}
                    {optional && <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}> · optional</span>}
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{item.description}</div>
            </div>
            <span style={{ fontSize: 12, color: '#1B75F0', fontWeight: 600, flexShrink: 0 }}>
                {item.done ? 'Done' : 'Set up'}
            </span>
        </button>
    );
}

export default GetReadyForPayroll;
